import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  Stack,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import { HelpOutline, WarningAmberOutlined } from "@mui/icons-material";
import type { ReactNode } from "react";

interface Props {
  open: boolean;
  title: string;
  /** Düz metin ya da zengin içerik (ör. sayıyı <strong> ile koyu göstermek için). */
  message: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  destructive?: boolean;
  loading?: boolean;
  /** Onay butonunu ayrıca kilitler (ör. yıkıcı işlemin etkisi hâlâ hesaplanıyorken). */
  confirmDisabled?: boolean;
  /** true → yalnız kapatma butonu gösterilir (bilgilendirme/engelleme diyaloğu; onay butonu yok). */
  hideConfirm?: boolean;
  onConfirm: () => void;
  onClose: () => void;
}

export const ConfirmDialog = ({
  open,
  title,
  message,
  confirmLabel = "Onayla",
  cancelLabel = "Vazgeç",
  destructive,
  loading,
  confirmDisabled,
  hideConfirm,
  onConfirm,
  onClose,
}: Props) => {
  const theme = useTheme();
  const tone = destructive ? theme.palette.error.main : theme.palette.primary.main;
  const Icon = destructive ? WarningAmberOutlined : HelpOutline;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
      <DialogContent sx={{ pt: 3 }}>
        <Stack direction="row" spacing={1.75} alignItems="flex-start">
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: "50%",
              flexShrink: 0,
              display: "grid",
              placeItems: "center",
              bgcolor: alpha(tone, 0.12),
              color: tone,
              "& svg": { fontSize: 22 },
            }}
          >
            <Icon />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
              {title}
            </Typography>
            <DialogContentText sx={{ fontSize: 14 }}>{message}</DialogContentText>
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading} color="inherit">
          {cancelLabel}
        </Button>
        {!hideConfirm && (
          <Button
            onClick={onConfirm}
            variant="contained"
            color={destructive ? "error" : "primary"}
            disabled={loading || confirmDisabled}
          >
            {confirmLabel}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};
